import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "loading", "error", "content",
    "prevButton", "nextButton", "weekRange",
    "days", "slotsContainer", "slots", "noSlots", "emptyWeek",
    "selectedSlot"
  ]

  static values = {
    apiUrl: { type: String, default: "/api/slots" }
  }

  connect() {
    this.currentWeekStart = this.getMonday(new Date())
    this.selectedDate = null
    this.slotsData = null
    this.hasJumpedToFirstAvailable = false

    this.fetchSlots()
  }

  getMonday(date) {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    d.setDate(diff)
    d.setHours(0, 0, 0, 0)
    return d
  }

  // Format date as YYYY-MM-DD using local timezone (avoids UTC conversion issues)
  formatDateForApi(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  async fetchSlots() {
    this.showLoading()

    try {
      const weekParam = this.formatDateForApi(this.currentWeekStart)
      const response = await fetch(`${this.apiUrlValue}?week=${weekParam}`)

      if (!response.ok) {
        throw new Error("Erreur lors de la recuperation des creneaux")
      }

      this.slotsData = await response.json()

      // If no slots this week and we haven't already jumped, go to first available week
      if (this.slotsData.days.length === 0 && this.slotsData.firstAvailableWeek && !this.hasJumpedToFirstAvailable) {
        this.hasJumpedToFirstAvailable = true
        this.currentWeekStart = this.getMonday(new Date(this.slotsData.firstAvailableWeek))
        this.fetchSlots()
        return
      }

      this.renderCalendar()

    } catch (error) {
      console.error("Error fetching slots:", error)
      this.showError()
    }
  }

  showLoading() {
    this.loadingTarget.classList.remove("hidden")
    this.errorTarget.classList.add("hidden")
    this.contentTarget.classList.add("hidden")
  }

  showError() {
    this.loadingTarget.classList.add("hidden")
    this.errorTarget.classList.remove("hidden")
    this.contentTarget.classList.add("hidden")
  }

  showContent() {
    this.loadingTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
    this.contentTarget.classList.remove("hidden")
  }

  renderCalendar() {
    this.showContent()

    // Update week range
    const weekEnd = new Date(this.currentWeekStart)
    weekEnd.setDate(weekEnd.getDate() + 6)

    const formatter = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long"
    })

    this.weekRangeTarget.textContent = `${formatter.format(this.currentWeekStart)} - ${formatter.format(weekEnd)}`

    // Update navigation buttons
    this.prevButtonTarget.disabled = !this.canGoPrevious()
    this.nextButtonTarget.disabled = !this.canGoNext()

    // Render days
    if (this.slotsData && this.slotsData.days.length > 0) {
      this.emptyWeekTarget.classList.add("hidden")
      this.daysTarget.innerHTML = ""

      this.slotsData.days.forEach(day => {
        const button = document.createElement("button")
        button.type = "button"
        button.className = `p-3 rounded-xl text-center transition-all ${
          this.selectedDate === day.date
            ? "bg-terracotta text-white shadow-md"
            : "bg-ivory hover:bg-beige text-charbon"
        }`
        button.dataset.action = "click->calendar#selectDay"
        button.dataset.date = day.date

        button.innerHTML = `
          <div class="text-xs font-medium">${day.dayName}</div>
          <div class="text-sm">${this.formatDayDate(day.date)}</div>
          <div class="text-xs mt-1 opacity-70">${day.slots.length} dispo${day.slots.length > 1 ? "s" : ""}</div>
        `

        this.daysTarget.appendChild(button)
      })

      // Auto-select first day if none selected
      if (!this.selectedDate && this.slotsData.days.length > 0) {
        this.selectedDate = this.slotsData.days[0].date
        this.renderDays()
        this.renderSlots()
      } else if (this.selectedDate) {
        this.renderSlots()
      }
    } else {
      this.emptyWeekTarget.classList.remove("hidden")
      this.daysTarget.innerHTML = ""
      this.slotsContainerTarget.classList.add("hidden")
    }
  }

  renderDays() {
    const buttons = this.daysTarget.querySelectorAll("button")
    buttons.forEach(button => {
      const date = button.dataset.date
      if (date === this.selectedDate) {
        button.className = "p-3 rounded-xl text-center transition-all bg-terracotta text-white shadow-md"
      } else {
        button.className = "p-3 rounded-xl text-center transition-all bg-ivory hover:bg-beige text-charbon"
      }
    })
  }

  renderSlots() {
    const dayData = this.slotsData?.days.find(d => d.date === this.selectedDate)

    if (!dayData || dayData.slots.length === 0) {
      this.slotsContainerTarget.classList.remove("hidden")
      this.slotsTarget.classList.add("hidden")
      this.noSlotsTarget.classList.remove("hidden")
      return
    }

    this.slotsContainerTarget.classList.remove("hidden")
    this.slotsTarget.classList.remove("hidden")
    this.noSlotsTarget.classList.add("hidden")

    this.slotsTarget.innerHTML = ""

    dayData.slots.forEach(slot => {
      const button = document.createElement("button")
      button.type = "button"
      const isSelected = this.selectedSlotTarget.value === slot.start
      button.className = `py-3 px-4 rounded-xl text-center font-medium transition-all ${
        isSelected
          ? "bg-vert text-white shadow-md"
          : "bg-ivory hover:bg-safran/30 text-charbon"
      }`
      button.dataset.action = "click->calendar#selectSlot"
      button.dataset.slot = slot.start
      button.textContent = slot.formatted

      this.slotsTarget.appendChild(button)
    })
  }

  selectDay(event) {
    this.selectedDate = event.currentTarget.dataset.date
    this.renderDays()
    this.renderSlots()
  }

  selectSlot(event) {
    const slot = event.currentTarget.dataset.slot
    this.selectedSlotTarget.value = slot

    // Update slot buttons
    const buttons = this.slotsTarget.querySelectorAll("button")
    buttons.forEach(button => {
      if (button.dataset.slot === slot) {
        button.className = "py-3 px-4 rounded-xl text-center font-medium transition-all bg-vert text-white shadow-md"
      } else {
        button.className = "py-3 px-4 rounded-xl text-center font-medium transition-all bg-ivory hover:bg-safran/30 text-charbon"
      }
    })

    // Notify booking controller
    const bookingController = this.element.closest("[data-controller~='booking']")
    if (bookingController) {
      const booking = this.application.getControllerForElementAndIdentifier(bookingController, "booking")
      if (booking) {
        booking.slotSelected(slot)
      }
    }
  }

  previousWeek() {
    if (!this.canGoPrevious()) return

    // Jump to previous available week if known
    if (this.slotsData?.previousAvailableWeek) {
      this.currentWeekStart = this.getMonday(new Date(this.slotsData.previousAvailableWeek))
    } else {
      const newDate = new Date(this.currentWeekStart)
      newDate.setDate(newDate.getDate() - 7)
      this.currentWeekStart = newDate
    }
    this.selectedDate = null
    this.fetchSlots()
  }

  nextWeek() {
    if (!this.canGoNext()) return

    // Jump to next available week if known
    if (this.slotsData?.nextAvailableWeek) {
      this.currentWeekStart = this.getMonday(new Date(this.slotsData.nextAvailableWeek))
    } else {
      const newDate = new Date(this.currentWeekStart)
      newDate.setDate(newDate.getDate() + 7)
      this.currentWeekStart = newDate
    }
    this.selectedDate = null
    this.fetchSlots()
  }

  canGoPrevious() {
    // Can go back if there's a previous available week
    return !!this.slotsData?.previousAvailableWeek
  }

  canGoNext() {
    // Can go forward if there's a next available week
    return !!this.slotsData?.nextAvailableWeek
  }

  formatDayDate(dateStr) {
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short"
    }).format(date)
  }
}
